// modal-system.interfaces.ts

// ──────────────────────────────────────────────────────────────
// modal_config — lives inside field_config JSONB on each column
// ──────────────────────────────────────────────────────────────

export interface ModalConfig {
  modal_type: 'status_timeline' | 'technician' | 'status_note' | 'labor_review' | 'notes' | 'appointment_window';
  dialog_title?: string;
  dialog_width?: string;
}

export interface LaborReviewEstimate {
  lading_labour_review_uuid: string;
  domain_uuid: string;
  lading_uuid: string;
  customer_contact_uuid: string | null;
  carrier_contact_uuid: string | null;
  estimated_install_minutes: number | null;
  estimated_handling_minutes: number | null;
  estimated_total_minutes: number | null;
  estimated_people: number | null;
  requested_install_minutes: number | null;
  requested_handling_minutes: number | null;
  requested_total_minutes: number | null;
  requested_people: number | null;
  requested_technician_drive_hours: number | null;
  requested_technician_hotel_nights: number;
  requested_notes: string | null;
  requested_by: string | null;
  requested_at: string | null;
  approved_install_minutes: number | null;
  approved_handling_minutes: number | null;
  approved_total_minutes: number | null;
  approved_people: number | null;
  approved_notes: string | null;
  approved_by: string | null;
  approved_at: string | null;
  status: string | null;
  rerate_requested: boolean | null;
  rerate_completed: boolean | null;
  rerate_completed_at: string | null;
  actual_started_at: string | null;
  actual_completed_at: string | null;
  created: string | null;
  updated: string | null;
}

export interface LaborRequestPayload {
  action?: 'request';
  lading_uuid: string;
  requested_install_minutes: number;
  requested_handling_minutes: number;
  requested_people: number | null;
  requested_technician_drive_hours: number | null;
  requested_technician_hotel_nights: number;
  requested_notes: string | null;
}

export interface LaborActualTimePayload {
  action: 'save_actual_time';
  lading_uuid: string;
  actual_started_at: string | null;
  actual_completed_at: string | null;
}

// ──────────────────────────────────────────────────────────────
// v_lading_status_timelines
// ──────────────────────────────────────────────────────────────

export interface LadingStatusTimeline {
  lading_status_timeline_uuid: string;
  domain_uuid: string;
  lading_uuid: string;
  created: string;
  lading_status: string;
  modified_time: string | null;
  latitude: string | null;
  longitude: string | null;
  lading_status_note: string | null;
  lading_status_updated: string | null;
  time_zone_id: string | null;
  local_time: string | null;
}

export interface StatusTimelinePayload {
  action: 'list' | 'create' | 'update' | 'delete';
  lading_uuid: string;
  lading_status?: string;
  lading_status_note?: string | null;
  local_time?: string | null;
  time_zone_id?: string | null;
  lading_status_timeline_uuid?: string;
}

export interface LadingStatusNote {
  lading_status_timeline_uuid: string;
  modified_time: string | null;
  lading_status_note: string | null;
}

// ──────────────────────────────────────────────────────────────
// technicians + technician_assignments
// ──────────────────────────────────────────────────────────────

export interface Technician {
  technician_uuid: string;
  technician_provider_uuid: string | null;
  technician_type_uuid: string | null;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  active: boolean;
  timezone: string | null;
  city: string | null;
  region: string | null;
  country: string | null;
  provider_name?: string;   // joined from technician_providers
  type_name?: string;       // joined from technician_types
}

export interface TechnicianAssignment {
  technician_assignment_uuid: string;
  technician_uuid: string | null;
  lading_uuid: string;
  assigned_at: string;
  accepted_at: string | null;
  completed_at: string | null;
  distance_meters: number | null;
  status: string;
  first_name?: string;      // joined from technicians
  last_name?: string;       // joined from technicians
  phone?: string;           // joined from technicians
  email?: string;           // joined from technicians
  provider_name?: string;   // joined from technician_providers
}

export interface TechnicianPayload {
  action: 'list_assignments' | 'list_available' | 'assign' | 'unassign' | 'update_status' | 'create_and_assign' | 'create_provider';
  lading_uuid: string;
  technician_uuid?: string;
  technician_assignment_uuid?: string;
  status?: string;
  search?: string;
  // create_and_assign fields
  first_name?: string;
  last_name?: string;
  phone?: string;
  email?: string;
  city?: string;
  region?: string;
  technician_provider_uuid?: string;
  // create_provider fields
  provider_name?: string;
}

// ──────────────────────────────────────────────────────────────
// Generic API response wrapper
// ──────────────────────────────────────────────────────────────

export interface ApiResponse<T = any> {
  success?: boolean;
  message?: string;
  code?: number;
  data?: T;
}

// ──────────────────────────────────────────────────────────────
// Status business-logic constants
// ──────────────────────────────────────────────────────────────

export const ADD_ONCE_STATUSES: ReadonlySet<string> = new Set([
  'Booked',
  'Trip Number Assigned',
  'Arrived Shipper',
  'Loading Shipper',
  'Loaded Shipper',
  'Arrived Consignee',
  'Unloading Consignee',
  'Delivered Consignee',
  'Installation complete',
  'Canceled',
  'Cancelled',
  'Order Accepted',
  'Accepted',
]);

export const ALLOW_MULTIPLE_STATUSES: ReadonlySet<string> = new Set([
  'In Transit',
  'Delayed',
  'Delay Traffic',
  'Delay Weather',
  'Delay Inspection',
  'Detained Shipper',
  'Detained Consignee',
  'Detained Customs',
  'Load Check',
  'En Route Shipper',
  'En Route Consignee',
]);

export const ALL_STATUSES: readonly string[] = [
  'Booked',
  'Order Accepted',
  'Accepted',
  'Trip Number Assigned',
  'En Route Shipper',
  'Arrived Shipper',
  'Loading Shipper',
  'Loaded Shipper',
  'In Transit',
  'En Route Consignee',
  'Arrived Consignee',
  'Unloading Consignee',
  'Delivered Consignee',
  'Installation complete',
  'Delayed',
  'Delay Traffic',
  'Delay Weather',
  'Delay Inspection',
  'Detained Shipper',
  'Detained Consignee',
  'Detained Customs',
  'Load Check',
  'Canceled',
  'Cancelled',
];
